import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { OrderDocument, PaginationParams } from '@my-account/const';
import { OrderDocumentService } from '@my-account/feature';
import { SdvorCustomPaginationComponent, SdvorInputAutocompleteComponent } from '@sdvor/shared/ui';
import { CustomPaginationModel } from '@shared/models';
import { ScrollToService } from '@shared/v2';
import { CurrentProfileService } from '@user/feature';
import { Observable, Subject, Subscription, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith, tap } from 'rxjs/operators';
import { OrderDocumentsTableComponent } from './order-documents-table';

const Options = {
  pageInitialValue: 0,
  searchPhraseInitialValue: '',
  pageSize: 20,
  searchDebounceTime: 300,
  searchPhraseMinLength: 2,
  searchPhraseMaxLength: 255,
};
@Component({
  selector: 'sd-order-documents',
  templateUrl: './order-documents.component.html',
  styleUrls: ['./order-documents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    SdvorInputAutocompleteComponent,
    AsyncPipe,
    OrderDocumentsTableComponent,
    SdvorCustomPaginationComponent,
  ],
})
export class OrderDocumentsComponent implements OnInit, OnDestroy {
  pagination$: Observable<CustomPaginationModel>;
  searchResult$: Observable<OrderDocument[]>;
  searchLoading$: Observable<boolean>;
  searchSuccess$: Observable<boolean>;
  searchError$: Observable<boolean>;

  changeSearchPhraseEvent$ = new Subject<string>();
  changeCurrentPageEvent$ = new Subject<number>();

  searchPhraseMinLength = Options.searchPhraseMinLength;
  searchPhraseMaxLength = Options.searchPhraseMaxLength;
  pageSize = Options.pageSize;

  private organizationUid$: Observable<string>;
  private searchPhrase$: Observable<string>;
  private paginationParams$: Observable<PaginationParams>;
  private subscription$ = new Subscription();

  constructor(
    private orderDocumentService: OrderDocumentService,
    private currentProfileService: CurrentProfileService,
    private scrollToService: ScrollToService,
    private viewRef: ViewContainerRef,
  ) {}

  ngOnInit(): void {
    this.pagination$ = this.orderDocumentService.getSearchValue().pipe(map((v) => v?.pagination));
    this.searchResult$ = this.orderDocumentService.getSearchValue().pipe(map((v) => v?.results || []));
    this.searchLoading$ = this.orderDocumentService.getSearchLoading();
    this.searchSuccess$ = this.orderDocumentService.getSearchSuccess();
    this.searchError$ = this.orderDocumentService.getSearchError();
    this.organizationUid$ = this.currentProfileService.get().pipe(
      filter((profile) => Boolean(profile.isLegal)),
      map((profile) => profile.selectedCompanyUid),
    );
    this.searchPhrase$ = this.changeSearchPhraseEvent$.pipe(
      startWith(Options.searchPhraseInitialValue),
      debounceTime(Options.searchDebounceTime),
      distinctUntilChanged(),
    );
    this.paginationParams$ = this.changeCurrentPageEvent$.pipe(
      tap(() => {
        this.scrollToService.scrollToElement(this.viewRef.element.nativeElement);
      }),
      startWith(Options.pageInitialValue),
      map((currentPage) => ({
        currentPage,
        pageSize: Options.pageSize,
      })),
    );

    this.subscription$.add(
      combineLatest([this.organizationUid$, this.searchPhrase$, this.paginationParams$]).subscribe(
        ([organizationUid, searchPhrase, paginationParams]) => {
          this.orderDocumentService.search({
            ...paginationParams,
            organizationUid,
            searchPhrase,
          });
        },
      ),
    );
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
    this.orderDocumentService.resetSearchState();
  }
}
